// src/components/Auth/VerifySignupPhone.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const VerifySignupPhone: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, email } = location.state || {};

  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0); // 0: enter phone, 1: enter code, 2: success
  const [phoneFormatted, setPhoneFormatted] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes

  useEffect(() => {
    // Redirect to login if no userId (user navigated here directly)
    if (!userId) {
      navigate('/login');
    }
  }, [userId, navigate]);

  useEffect(() => {
    // Countdown timer for code expiration
    if (codeSent && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [codeSent, timeRemaining]);

  const formatPhoneForDisplay = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Step 1: Validate phone number
      const validateResponse = await api.post('/endpoints/verify-signup-phone.php?action=validate', {
        phone_number: phoneNumber,
        user_id: userId,
      });

      if (!validateResponse.data.success) {
        setError(validateResponse.data.message || 'Phone validation failed');
        setLoading(false);
        return;
      }

      const validation = validateResponse.data.data;

      if (!validation.valid_for_2fa) {
        setError(validation.message || 'This phone number is not valid for verification');
        setLoading(false);
        return;
      }

      // Step 2: Send verification code
      const sendCodeResponse = await api.post('/endpoints/verify-signup-phone.php?action=send_code', {
        phone_number: phoneNumber,
        user_id: userId,
      });

      if (sendCodeResponse.data.success) {
        setPhoneFormatted(validation.formatted);
        setCodeSent(true);
        setStep(1);
        setTimeRemaining(600);
      } else {
        setError(sendCodeResponse.data.message || 'Failed to send verification code');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send verification code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await api.post('/endpoints/verify-signup-phone.php?action=verify_code', {
        phone_number: phoneNumber,
        user_id: userId,
        code: verificationCode,
      });

      if (response.data.success) {
        setStep(2);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err: any) {
      // Check if this is a payment failure error
      const errorData = err.response?.data;

      if (errorData?.error_type === 'payment_declined' && errorData?.can_retry) {
        // Payment declined - show retry option
        const confirmRetry = window.confirm(
          `${errorData.message}\n\n${errorData.details}\n\nWould you like to go back to update your payment information?`
        );

        if (confirmRetry) {
          // Redirect to signup page to update payment details
          // You could store userId in sessionStorage to allow them to update payment
          sessionStorage.setItem('retry_payment_user_id', userId);
          sessionStorage.setItem('retry_payment_phone', phoneNumber);
          navigate('/signup', { state: { retryPayment: true, userId } });
        } else {
          setError('Payment processing failed. Please contact support to resolve this issue.');
        }
      } else {
        // Other errors (invalid code, etc.)
        const errorMessage = errorData?.message || 'Verification failed';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await api.post('/endpoints/verify-signup-phone.php?action=send_code', {
        phone_number: phoneNumber,
        user_id: userId,
      });

      if (response.data.success) {
        setTimeRemaining(600);
        setVerificationCode('');
        setError(null);
      } else {
        setError(response.data.message || 'Failed to resend code');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to resend code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Step 2: Success
  if (step === 2) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 2, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Account Verified Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Your phone has been verified and your account is now fully activated.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You will receive an email with your softphone credentials shortly.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Redirecting to login page...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%', mx: 2 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h1">
            Verify Your Phone Number
          </Typography>
        </Box>

        <Stepper activeStep={step} sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Enter Phone Number</StepLabel>
          </Step>
          <Step>
            <StepLabel>Verify Code</StepLabel>
          </Step>
        </Stepper>

        {email && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Email verified: <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Now verify your Canadian mobile phone number to complete signup.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 0: Enter phone number */}
        {step === 0 && (
          <form onSubmit={handleSendCode}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please enter your Canadian mobile phone number. We'll send you a verification code via SMS.
            </Typography>

            <TextField
              fullWidth
              type="tel"
              label="Mobile Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="(555) 123-4567"
              margin="normal"
              required
              helperText="Must be a Canadian mobile number"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !phoneNumber}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
            </Button>
          </form>
        )}

        {/* Step 1: Enter verification code */}
        {step === 1 && (
          <form onSubmit={handleVerifyCode}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Verification code sent to {formatPhoneForDisplay(phoneNumber)}
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the 6-digit code sent to your phone. Code expires in {formatTime(timeRemaining)}.
            </Typography>

            <TextField
              fullWidth
              type="text"
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              margin="normal"
              required
              inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || verificationCode.length !== 6}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify Code'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{' '}
                <Button
                  onClick={handleResendCode}
                  disabled={loading || timeRemaining > 540}
                  sx={{ textTransform: 'none', p: 0 }}
                >
                  Resend Code
                </Button>
              </Typography>
              <Button
                onClick={() => {
                  setStep(0);
                  setCodeSent(false);
                  setVerificationCode('');
                  setError(null);
                }}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Change Phone Number
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default VerifySignupPhone;
